"use client";

import * as React from "react";
import { motion } from "framer-motion";
import { Calculator, CalendarRange, Workflow, ChefHat, Bath, ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
interface ResourceCardItem {
  id: string;
  title: string;
  description: string;
  to: string;
  icon: React.ComponentType<{
    className?: string;
  }>;
}
const RESOURCES: ResourceCardItem[] = [{
  id: "cost-guide",
  title: "Remodeling Cost Guide",
  description: "Understand budget ranges, square footage cost estimates, and real investment factors for Decatur renovations.",
  to: "/DecaturRemodelingCostGuidePage",
  icon: Calculator
}, {
  id: "planning-guide",
  title: "Remodeling Planning Guide",
  description: "Navigate structural feasibility, local permitting, architectural milestones, and realistic project timelines.",
  to: "/DecaturRemodelingPlanningGuidePage",
  icon: CalendarRange
}, {
  id: "process-planning",
  title: "Process & Planning",
  description: "Explore our collaborative design-build framework, from initial consultation through final punch list walk-through.",
  to: "/ProcessPlanningPage",
  icon: Workflow
}, {
  id: "kitchen-remodeling",
  title: "Kitchen Remodeling",
  description: "Custom cabinetry, expanded islands, and high-performance culinary layouts designed for gathering and entertaining.",
  to: "/KitchenRemodelingPage",
  icon: ChefHat
}, {
  id: "bathroom-remodeling",
  title: "Bathroom Remodeling",
  description: "Transform primary suites, walk-in showers, and historic powder rooms with enduring stone and precision fixtures.",
  to: "/BathroomRemodelingPage",
  icon: Bath
}];
const containerVariants = {
  hidden: {
    opacity: 0
  },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.08,
      delayChildren: 0.1
    }
  }
};
const cardVariants = {
  hidden: {
    opacity: 0,
    y: 16
  },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.4,
      ease: [0.16, 1, 0.3, 1] as [number, number, number, number]
    }
  }
};
export default function RelatedGuidesAndServices() {
  return <section id="relatedguidesandservices" data-nav="light" className="relative bg-background text-foreground py-20 lg:py-28 overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Header Block */}
        <div className="text-center max-w-2xl mx-auto mb-14 lg:mb-16">
          <p className="text-xs font-semibold tracking-widest text-chart-1 uppercase mb-3">
            Resources & Expertise
          </p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-foreground">
            Explore Related Guides & Services
          </h2>
          <div className="w-12 h-0.5 bg-chart-1 mx-auto mt-4 rounded-full" aria-hidden="true" />
        </div>

        {/* Multi-Column Cards Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-60px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8 justify-center">
          {RESOURCES.map((item, index) => {
          const Icon = item.icon;
          return <motion.div key={item.id} data-index={index} variants={cardVariants} whileHover={{
            y: -4
          }} transition={{
            duration: 0.25,
            ease: "easeOut"
          }} className={index >= 3 ? "lg:col-span-1 md:last:col-span-2 lg:last:col-span-1" : ""}>
                <Card className="h-full bg-card/85 backdrop-blur-xs border border-border rounded-xl shadow-xs hover:shadow-md transition-shadow duration-300 flex flex-col justify-between p-6 sm:p-7">
                  <CardContent className="p-0 flex flex-col gap-4">
                    <div className="size-11 rounded-lg bg-secondary/80 text-chart-1 flex items-center justify-center shrink-0 border border-border">
                      <Icon className="size-5" />
                    </div>

                    <div className="space-y-2">
                      <h3 className="font-serif text-xl font-medium text-foreground tracking-tight">
                        {item.title}
                      </h3>
                      <p className="text-sm text-muted-foreground leading-relaxed">
                        {item.description}
                      </p>
                    </div>
                  </CardContent>

                  <div className="pt-6 mt-auto border-t border-border/60">
                    <Button variant="ghost" size="sm" asChild className="px-0 text-foreground hover:text-chart-1 font-medium group hover:bg-transparent justify-start">
                      <Link to={item.to} className="inline-flex items-center gap-1.5">
                        <span>View details</span>
                        <ArrowRight className="size-4 transition-transform duration-200 group-hover:translate-x-1" />
                      </Link>
                    </Button>
                  </div>
                </Card>
              </motion.div>;
        })}
        </motion.div>
      </div>
    </section>;
}